/* Template preview panel — dockable (right / bottom), resizable, auto-refresh */

import { state, refs } from './state.js';
import { animateGrid } from './diff-panel.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const DOCK_KEY = 'agentcpq_preview_dock';     // localStorage key for dock position
const RIGHT_W_KEY = 'agentcpq_preview_rw';    // localStorage key for right width
const BOTTOM_H_KEY = 'agentcpq_preview_bh';   // localStorage key for bottom height
const RIGHT_MIN = 250;
const RIGHT_MAX = 800;
const BOTTOM_MIN = 150;
const BOTTOM_MAX_RATIO = 0.7; // max 70% of viewport height

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------
let previewOpen = false;
let previewMode = 'mock'; // 'mock' | 'ground_truth'
let dockPosition = 'right'; // 'right' | 'bottom'
let sseSource = null;

// ---------------------------------------------------------------------------
// DOM references (lazy)
// ---------------------------------------------------------------------------
function getPanel() { return document.getElementById('previewPanel'); }
function getIframe() { return document.getElementById('previewIframe'); }
function getModeLabel() { return document.getElementById('previewModeLabel'); }
function getInitBtn() { return document.getElementById('btnInitPreview'); }
function getResizeHandle() { return document.getElementById('previewResizeHandle'); }

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function showPreviewPanel() {
  if (previewOpen) return;

  previewOpen = true;
  const cls = dockPosition === 'bottom' ? 'preview-bottom' : 'preview-open';
  animateGrid(() => refs.app.classList.add(cls));
  connectSSE();
  refreshPreview();
}

export function hidePreviewPanel() {
  if (!previewOpen) return;
  previewOpen = false;
  animateGrid(() => {
    refs.app.classList.remove('preview-open', 'preview-bottom');
  });
  disconnectSSE();
}

export function togglePreviewPanel() {
  if (previewOpen) hidePreviewPanel();
  else showPreviewPanel();
}

export function isPreviewOpen() { return previewOpen; }

export function switchToGroundTruth(url) {
  previewMode = 'ground_truth';
  const iframe = getIframe();
  if (iframe) iframe.src = url;
  const label = getModeLabel();
  if (label) label.textContent = 'Live';
}

export function switchToMockPreview() {
  previewMode = 'mock';
  refreshPreview();
  const label = getModeLabel();
  if (label) label.textContent = 'Preview';
}

// ---------------------------------------------------------------------------
// Dock position switching
// ---------------------------------------------------------------------------

function setDockPosition(pos) {
  if (pos === dockPosition) return;
  const wasOpen = previewOpen;

  if (wasOpen) {
    // Remove current layout class without animation
    refs.app.classList.remove('preview-open', 'preview-bottom');
  }

  dockPosition = pos;
  localStorage.setItem(DOCK_KEY, pos);

  // Update resize handle cursor
  const handle = getResizeHandle();
  if (handle) {
    handle.style.cursor = pos === 'bottom' ? 'row-resize' : 'col-resize';
  }

  // Update dock button active states
  document.querySelectorAll('.preview-dock-btn').forEach(btn => {
    btn.classList.toggle('active', btn.dataset.dock === pos);
  });

  if (wasOpen) {
    const cls = pos === 'bottom' ? 'preview-bottom' : 'preview-open';
    animateGrid(() => refs.app.classList.add(cls));
  }
}

// ---------------------------------------------------------------------------
// Preview refresh
// ---------------------------------------------------------------------------

function refreshPreview() {
  const iframe = getIframe();
  if (!iframe) return;
  if (previewMode === 'mock') {
    iframe.src = '/api/template/preview/?t=' + Date.now();
  }
}

// ---------------------------------------------------------------------------
// Resize logic
// ---------------------------------------------------------------------------

function initResize() {
  const handle = getResizeHandle();
  if (!handle) return;

  // Restore saved sizes
  const savedRW = localStorage.getItem(RIGHT_W_KEY);
  if (savedRW) {
    const w = parseInt(savedRW, 10);
    if (w >= RIGHT_MIN && w <= RIGHT_MAX) {
      document.documentElement.style.setProperty('--preview-w', w + 'px');
    }
  }
  const savedBH = localStorage.getItem(BOTTOM_H_KEY);
  if (savedBH) {
    const h = parseInt(savedBH, 10);
    if (h >= BOTTOM_MIN) {
      document.documentElement.style.setProperty('--preview-h', h + 'px');
    }
  }

  let startPos, startSize;

  function onMouseMove(e) {
    if (dockPosition === 'right') {
      // Dragging left edge of right panel → width increases as mouse moves left
      const newW = Math.min(RIGHT_MAX, Math.max(RIGHT_MIN, startSize - (e.clientX - startPos)));
      document.documentElement.style.setProperty('--preview-w', newW + 'px');
    } else {
      // Dragging top edge of bottom panel → height increases as mouse moves up
      const maxH = window.innerHeight * BOTTOM_MAX_RATIO;
      const newH = Math.min(maxH, Math.max(BOTTOM_MIN, startSize - (e.clientY - startPos)));
      document.documentElement.style.setProperty('--preview-h', newH + 'px');
    }
  }

  function onMouseUp() {
    refs.app.classList.remove('preview-resizing');
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);

    // Persist
    const panel = getPanel();
    if (panel) {
      const rect = panel.getBoundingClientRect();
      if (dockPosition === 'right') {
        localStorage.setItem(RIGHT_W_KEY, String(Math.round(rect.width)));
      } else {
        localStorage.setItem(BOTTOM_H_KEY, String(Math.round(rect.height)));
      }
    }
  }

  handle.addEventListener('mousedown', (e) => {
    e.preventDefault();
    startPos = dockPosition === 'right' ? e.clientX : e.clientY;
    const panel = getPanel();
    startSize = panel
      ? (dockPosition === 'right' ? panel.getBoundingClientRect().width : panel.getBoundingClientRect().height)
      : 380;
    refs.app.classList.add('preview-resizing');
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  });
}

// ---------------------------------------------------------------------------
// SSE connection for auto-refresh
// ---------------------------------------------------------------------------

function connectSSE() {
  if (sseSource) return;
  sseSource = new EventSource('/api/template/preview/events');
  sseSource.onmessage = (evt) => {
    if (evt.data === 'preview_updated' && previewOpen && previewMode === 'mock') {
      refreshPreview();
    }
  };
  sseSource.onerror = () => {};
}

function disconnectSSE() {
  if (sseSource) {
    sseSource.close();
    sseSource = null;
  }
}

// ---------------------------------------------------------------------------
// WebSocket message handling (called from main.js)
// ---------------------------------------------------------------------------

export function handlePreviewWsMessage(msg) {
  if (msg.type === 'preview_updated' && previewOpen && previewMode === 'mock') {
    refreshPreview();
  }
}

// ---------------------------------------------------------------------------
// Init
// ---------------------------------------------------------------------------

export function initPreviewPanel() {
  // Restore dock position
  const savedDock = localStorage.getItem(DOCK_KEY);
  if (savedDock === 'bottom' || savedDock === 'right') {
    dockPosition = savedDock;
  }

  // Close button
  const closeBtn = document.getElementById('btnClosePreview');
  if (closeBtn) closeBtn.addEventListener('click', hidePreviewPanel);

  // Refresh Data button
  const initBtn = getInitBtn();
  if (initBtn) {
    initBtn.addEventListener('click', async () => {
      initBtn.disabled = true;
      initBtn.textContent = 'Loading...';
      try {
        const resp = await fetch('/api/template/preview/init', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        });
        const data = await resp.json();
        if (data.status === 'ok') {
          initBtn.textContent = 'Refresh Data';
          refreshPreview();
        } else {
          initBtn.textContent = 'Error: ' + (data.error || 'unknown');
        }
      } catch (e) {
        initBtn.textContent = 'Failed';
      } finally {
        initBtn.disabled = false;
        setTimeout(() => { initBtn.textContent = 'Refresh Data'; }, 3000);
      }
    });
  }

  // Mock button
  const mockBtn = document.getElementById('btnPreviewMock');
  if (mockBtn) mockBtn.addEventListener('click', switchToMockPreview);

  // Dock position buttons
  document.querySelectorAll('.preview-dock-btn').forEach(btn => {
    btn.addEventListener('click', () => setDockPosition(btn.dataset.dock));
    btn.classList.toggle('active', btn.dataset.dock === dockPosition);
  });

  // Resize handle
  initResize();

  // Add preview toggle button to topbar
  const topbarRight = document.querySelector('.topbar-right');
  if (topbarRight) {
    const btn = document.createElement('button');
    btn.className = 'btn-icon topbar-preview-btn';
    btn.id = 'btnTogglePreview';
    btn.title = 'Toggle Template Preview';
    btn.innerHTML = `<i class="ic ic-columns-2" style="font-size:16px"></i>`;
    btn.addEventListener('click', togglePreviewPanel);
    const modelSelect = topbarRight.querySelector('.select-model');
    if (modelSelect) topbarRight.insertBefore(btn, modelSelect);
    else topbarRight.prepend(btn);
  }
}
